import { Box, Card, Typography, Grid, useTheme } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { getChartColors } from '../utils/chartConfig';

/**
 * CapitalAccountingSection - Capital Preservation & Accounting Treatment
 *
 * Shows working capital preservation and CapEx vs OpEx comparison.
 * Matches Print View Page 10 content.
 */
export default function CapitalAccountingSection({ roiResults, paymentMetrics }) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);
  const summary = roiResults?.portfolio?.summary || {};
  const totalCost = summary.total_treatment_cost || 0;

  // Capital preservation calculations (3-year deferred)
  const year1Retained = totalCost * (2 / 3); // 2/3 still available in year 1
  const year2Retained = totalCost * (1 / 3); // 1/3 still available in year 2
  const averageCapitalAvailable = totalCost * 0.5; // Average over 3 years

  const formatCurrency = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '$0';
    if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
    if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${val.toFixed(0)}`;
  };

  const accountingComparison = {
    upfront: {
      type: 'CapEx',
      treatment: 'Capitalized asset',
      depreciation: 'Over useful life',
      taxImpact: 'Spread over depreciation',
      budgetApproval: 'CapEx approval required',
    },
    threeYear: {
      type: 'OpEx',
      treatment: 'Operating expense',
      depreciation: 'N/A - expensed',
      taxImpact: 'Full deduction in year',
      budgetApproval: 'Simpler OpEx budget',
    },
  };

  const comparisonRows = [
    { label: 'Classification', key: 'type' },
    { label: 'Treatment', key: 'treatment' },
    { label: 'Depreciation', key: 'depreciation' },
    { label: 'Tax Impact', key: 'taxImpact' },
    { label: 'Approval', key: 'budgetApproval' },
  ];

  return (
    <Box sx={{ mt: 4, mb: 3 }}>
      {/* Section Header */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          fontSize: '1.1rem',
          color: 'text.primary',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <AccountBalanceIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
        Capital & Accounting Treatment
      </Typography>

      {/* Capital Preservation Analysis */}
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: 'text.secondary',
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Capital Preservation Analysis
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.5 }}>
        Working capital retained in your business vs paying upfront
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 2,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderTop: '3px solid #4CAF50',
              borderRadius: 1.5,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              Year 1 Capital Retained
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
                mb: 0.25,
              }}
            >
              {formatCurrency(year1Retained)}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
              Available for other uses
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 2,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderTop: '3px solid #4CAF50',
              borderRadius: 1.5,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              Year 2 Capital Retained
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
                mb: 0.25,
              }}
            >
              {formatCurrency(year2Retained)}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
              Available for other uses
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 2,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderTop: (theme) => `3px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              Avg Capital Available
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'text.primary',
                mb: 0.25,
              }}
            >
              {formatCurrency(averageCapitalAvailable)}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
              Over 3 years
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Accounting Treatment Comparison */}
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: 'text.secondary',
          mb: 1.5,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Accounting Treatment Comparison
      </Typography>

      <Grid container spacing={2}>
        {/* Upfront (CapEx) Column */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 2,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              height: '100%',
            }}
          >
            <Box
              sx={{
                mb: 2,
                pb: 1.5,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Upfront (CapEx)
              </Typography>
            </Box>
            {comparisonRows.map((row) => (
              <Box
                key={row.key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.primary', textAlign: 'right' }}>
                  {accountingComparison.upfront[row.key]}
                </Typography>
              </Box>
            ))}
          </Card>
        </Grid>

        {/* 3-Year (OpEx) Column - Highlighted */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.03) 100%)',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              borderRadius: 1.5,
              height: '100%',
            }}
          >
            <Box
              sx={{
                mb: 2,
                pb: 1.5,
                borderBottom: '1px solid rgba(76, 175, 80, 0.15)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
                }}
              >
                3-Year (OpEx) ★ Recommended
              </Typography>
            </Box>
            {comparisonRows.map((row) => (
              <Box
                key={row.key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid rgba(76, 175, 80, 0.15)',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.primary', fontWeight: 500, textAlign: 'right' }}>
                  {accountingComparison.threeYear[row.key]}
                </Typography>
              </Box>
            ))}
          </Card>
        </Grid>
      </Grid>

      {/* Disclaimer */}
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 1,
          background: 'rgba(255, 255, 255, 0.02)',
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.5 }}>
          <strong>Note:</strong> Consult your accountant for specific CapEx vs OpEx treatment in your jurisdiction.
          Tax implications may vary based on depreciation schedules and local regulations.
        </Typography>
      </Box>
    </Box>
  );
}
